import { Controller } from "@hotwired/stimulus"

// Tile + popover picker for Submission#media_type. Owns the visible trigger,
// the popover grid, and a hidden input that the cloudinary-upload controller
// reads / writes via its `mediaType` Stimulus target. Selecting a tile fires
// a `change` event on the hidden input, which the cloudinary-upload controller
// is already wired to react to via `change->cloudinary-upload#saveSubmissionDetails`.
export default class extends Controller {
  static targets = ["trigger", "triggerLabel", "triggerIcon", "popover", "hiddenInput", "aiBadge"]

  declare readonly triggerTarget: HTMLButtonElement
  declare readonly triggerLabelTarget: HTMLElement
  declare readonly triggerIconTarget: HTMLElement
  declare readonly popoverTarget: HTMLElement
  declare readonly hiddenInputTarget: HTMLInputElement
  declare readonly aiBadgeTarget: HTMLElement
  declare readonly hasAiBadgeTarget: boolean

  private boundClickOutside = this.clickOutside.bind(this)

  disconnect(): void {
    document.removeEventListener("click", this.boundClickOutside)
  }

  toggle(event: Event): void {
    event.stopPropagation()
    const isOpen = !this.popoverTarget.classList.contains("hidden")
    if (isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  private open(): void {
    this.popoverTarget.classList.remove("hidden")
    document.addEventListener("click", this.boundClickOutside)
  }

  private close(): void {
    this.popoverTarget.classList.add("hidden")
    document.removeEventListener("click", this.boundClickOutside)
  }

  private clickOutside(event: Event): void {
    if (!this.element.contains(event.target as Node)) {
      this.close()
    }
  }

  select(event: Event): void {
    const button = event.currentTarget as HTMLButtonElement
    const value = button.dataset.mediaType
    if (!value) return

    // Update the trigger UI
    this.triggerLabelTarget.textContent = value
    const newIcon = button.querySelector("svg")
    if (newIcon) {
      this.triggerIconTarget.innerHTML = ""
      const clone = newIcon.cloneNode(true) as SVGElement
      clone.setAttribute("class", "h-7 w-7")
      this.triggerIconTarget.appendChild(clone)
    }

    // Update selected styling on tiles
    this.popoverTarget.querySelectorAll<HTMLButtonElement>("[data-media-type]").forEach(btn => {
      const isSelected = btn === button
      btn.classList.toggle("border-accent", isSelected)
      btn.classList.toggle("bg-accent/10", isSelected)
      btn.classList.toggle("text-accent", isSelected)
      btn.classList.toggle("border-transparent", !isSelected)
      btn.classList.toggle("hover:bg-base-200", !isSelected)
    })

    // Persist via the hidden input + change event (cloudinary-upload listens)
    this.hiddenInputTarget.value = value
    this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }))

    // Optimistically clear the AI-detected badge — server flips source to
    // "manual" via SubmissionsController#update once the value-changed check
    // passes, and the next picker broadcast (if any) won't re-render the badge.
    if (this.hasAiBadgeTarget) {
      this.aiBadgeTarget.remove()
    }

    this.close()
  }
}
